import { useState, useEffect, useRef, useCallback } from "react";
import {
  FermentationOrchestrator,
  PHASES,
} from "@brewery/services/fermentationOrchestrator";

const IN_FLIGHT_PHASES = [
  PHASES.STOPPING,
  PHASES.AWAIT_IDLE,
  PHASES.STARTING,
  PHASES.ACKING,
];

export const KEEP_OPEN_COPY =
  "Keep this page open until fermentation restarts — closing now can " +
  "leave your keg stopped. Applying usually takes under 4 minutes.";

export const LEAVE_WARNING =
  "Settings are still being applied — leaving now can leave your keg " +
  "stopped. Leave anyway?";

export const STAGE_NOTE =
  "Your plan is applied as numbered steps — the first one is shown as " +
  "“Primary” in the portal even if you were further along.";

export const PHASE_STEPS = [
  { phase: PHASES.STOPPING, label: "Stopping current fermentation" },
  { phase: PHASES.AWAIT_IDLE, label: "Waiting for the keg to go idle" },
  { phase: PHASES.STARTING, label: "Starting new fermentation" },
  { phase: PHASES.ACKING, label: "Confirming fermentation prep" },
  { phase: PHASES.DONE, label: "Temperature control active" },
];

function useFermentationOrchestrator() {
  const [phase, setPhase] = useState(null);
  const [error, setError] = useState(null);
  const orchestratorRef = useRef(null);

  const inFlight = IN_FLIGHT_PHASES.includes(phase);

  const warnUnload = useCallback(
    (e) => {
      if (!inFlight) return;
      e.preventDefault();
      e.returnValue = "";
    },
    [inFlight],
  );

  useEffect(() => {
    window.addEventListener("beforeunload", warnUnload);
    return () => {
      window.removeEventListener("beforeunload", warnUnload);
    };
  }, [warnUnload]);

  useEffect(() => {
    return () => {
      if (orchestratorRef.current) {
        orchestratorRef.current.onPhase = () => {};
        orchestratorRef.current.cancel();
      }
    };
  }, []);

  const startOrchestration = useCallback(
    ({ deviceUuid, sessionId, mode, settings, beerRecipeId }) => {
      setError(null);
      const common = {
        deviceUuid,
        sessionId,
        mode,
        onPhase: (newPhase, newError) => {
          setPhase(newPhase);
          if (newError) setError(newError);
        },
      };

      const orch = new FermentationOrchestrator(
        beerRecipeId != null
          ? { ...common, beerRecipeId }
          : { ...common, settings },
      );
      orchestratorRef.current = orch;
      orch.run();
    },
    [],
  );

  const retry = useCallback(() => {
    if (orchestratorRef.current) {
      orchestratorRef.current.run();
    }
  }, []);

  const resolveConflictAndRetry = useCallback(async () => {
    if (orchestratorRef.current) {
      await orchestratorRef.current.resolveConflictAndRetry();
    }
  }, []);

  const reset = useCallback(() => {
    if (orchestratorRef.current) {
      orchestratorRef.current.cancel();
      orchestratorRef.current = null;
    }
    setPhase(null);
    setError(null);
  }, []);

  return {
    phase,
    error,
    inFlight,
    startOrchestration,
    retry,
    resolveConflictAndRetry,
    reset,
    setPhase,
    setError,
    orchestratorRef,
  };
}

export default useFermentationOrchestrator;
